/**
 * Currency utility functions for VND formatting
 */

const VND_SYMBOL = '₫'

/**
 * Format number as VND currency
 * Uses comma (,) as thousand separator per Vietnamese number format standards
 * Only displays integer part (no decimal places)
 */
export function formatVND(amount: number | undefined | null): string {
  // Handle invalid values
  if (amount === undefined || amount === null || isNaN(amount) || !isFinite(amount)) {
    return '0 ₫'
  }
  // Round to integer (no decimal places for VND)
  const integerAmount = Math.round(amount)
  // Format with comma as thousand separator (Vietnamese standard)
  const formatted = integerAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${formatted} ₫`
}

/**
 * Format number as VND with symbol
 */
export function formatVNDWithSymbol(amount: number): string {
  return `${formatNumber(amount)} ${VND_SYMBOL}`
}

/**
 * Format number with thousand separators (no currency symbol)
 * Uses comma (,) as thousand separator per Vietnamese number format standards
 * Only displays integer part (no decimal places)
 */
export function formatNumber(amount: number): string {
  if (isNaN(amount) || !isFinite(amount)) {
    return '0'
  }
  // Round to integer (no decimal places)
  const integerAmount = Math.round(amount)
  // Format with comma as thousand separator (Vietnamese standard)
  return integerAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Parse VND string to number (removes formatting)
 */
export function parseVND(vndString: string): number {
  // Remove all non-digit characters except decimal point
  const cleaned = vndString.replace(/[^\d.]/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format large numbers with abbreviations (e.g., 1.5B for 1,500,000,000)
 */
export function formatLargeNumber(amount: number): string {
  if (amount >= 1000000000) {
    return `${Math.round(amount / 1000000000)}B ${VND_SYMBOL}`
  }
  if (amount >= 1000000) {
    return `${Math.round(amount / 1000000)}M ${VND_SYMBOL}`
  }
  if (amount >= 1000) {
    return `${Math.round(amount / 1000)}K ${VND_SYMBOL}`
  }
  return formatVND(amount)
}

/**
 * Format number with thousand separators for input display (e.g., "1,000,000")
 * Returns empty string if value is 0 or NaN
 */
export function formatNumberForInput(value: number): string {
  if (!value || isNaN(value) || value === 0) {
    return ''
  }
  return formatNumber(value)
}

/**
 * Parse formatted number string back to number
 * Handles thousand separators and empty strings
 */
export function parseFormattedNumber(value: string): number {
  if (!value || value.trim() === '') {
    return 0
  }
  // Remove all non-digit characters
  const cleaned = value.replace(/[^\d]/g, '')
  const parsed = parseFloat(cleaned)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format number with thousand separators while preserving cursor position
 * This is used for real-time formatting during input
 */
export function formatNumberWithCursor(
  value: string,
  cursorPosition: number
): { formatted: string; newCursorPosition: number } {
  // Remove all non-digit characters to get raw number
  const digitsOnly = value.replace(/[^\d]/g, '')
  
  if (!digitsOnly) {
    return { formatted: '', newCursorPosition: 0 }
  }

  // Count digits before cursor position in original value
  const digitsBeforeCursor = value.substring(0, cursorPosition).replace(/[^\d]/g, '').length
  
  // Format the number
  const numValue = parseInt(digitsOnly, 10)
  if (isNaN(numValue)) {
    return { formatted: '', newCursorPosition: 0 }
  }
  const formatted = formatNumber(numValue)
  
  // Calculate new cursor position
  // We need to find where the cursor should be in the formatted string
  // by counting digits and commas
  let newCursorPos = 0
  let digitCount = 0
  
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] === ',') {
      // If we haven't reached the target digit yet, skip the comma
      if (digitCount < digitsBeforeCursor) {
        newCursorPos++
      } else {
        // We've passed the target, stop here
        break
      }
    } else {
      digitCount++
      newCursorPos++
      // If we've reached the target digit position, stop after this digit
      if (digitCount >= digitsBeforeCursor) {
        break
      }
    }
  }
  
  // Ensure cursor position is within bounds
  newCursorPos = Math.min(newCursorPos, formatted.length)

  return { formatted, newCursorPosition: newCursorPos }
}

